import { useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';

interface Person {
  id: string;
  name: string;
  username: string;
  roles: string[];
  active: boolean;
  locked: boolean;
  /** Signs in with a password and an authenticator app. */
  two_step: boolean;
  /** The e-mail this person signs in with through the business's provider. */
  sso_email: string | null;
  sso_linked: boolean;
}
export interface RoleInfo {
  name: string;
  /** The business's own roles carry their name; the others are translated by key. */
  label?: string | null;
  description?: string | null;
  custom?: boolean;
  rules?: { model: string; field: string }[];
  permissions: { name: string; label: string }[];
}
interface Data {
  people: Person[];
  roles: RoleInfo[];
  can_grant_owner: boolean;
  /** The business signs in through its own provider (Manage → Single sign-on). */
  sso: boolean;
}

const KNOWN_ROLES = ['owner', 'manager', 'accountant', 'sales', 'cashier', 'waiter', 'kitchen', 'reception', 'housekeeping', 'production', 'technician'];
/** Known roles have a translated name; a role added by a later module, or by the business, shows its own name. */
export const roleLabel = (r: string, roles?: readonly RoleInfo[]) => {
  const own = roles?.find((x) => x.name === r)?.label;
  if (own) return own;
  if (KNOWN_ROLES.includes(r)) return t(`role.${r}` as MessageKey);
  const bare = r.replace(/^custom\./, '').replace(/[-_]/g, ' ');
  return bare.charAt(0).toUpperCase() + bare.slice(1);
};
/** A one-line summary; the full list of permissions is one click away. */
export const roleHint = (r: RoleInfo) =>
  r.custom
    ? r.description || r.permissions.map((p) => p.label).join(' · ') || t('staff.no_permissions')
    : KNOWN_ROLES.includes(r.name)
      ? t(`role.${r.name}_hint` as MessageKey)
      : r.permissions.map((p) => p.label).join(' · ') || t('staff.no_permissions');
export const roleOrder = (r: string) => (KNOWN_ROLES.includes(r) ? KNOWN_ROLES.indexOf(r) : r.startsWith('custom.') ? 100 : 99);

export function Staff() {
  const { data, reload } = useAction<Data>('staff.list');
  const [editing, setEditing] = useState<Person | 'new' | null>(null);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const people = (data?.people ?? [])
    .filter((p) => !q || p.name.toLowerCase().includes(q) || p.username.includes(q))
    .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));

  return (
    <ManageLayout
      title={t('manage.staff')}
      subtitle={data ? tn('staff.summary', data.people.filter((p) => p.active).length) : t('staff.subtitle')}
      actions={
        <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
          {t('staff.add')}
        </Button>
      }
    >
      <div className="toolbar">
        <div className="search grow">
          <Icon name="search" size={18} />
          <input className="input" type="search" placeholder={t('staff.search')} aria-label={t('staff.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      </div>
      {!data ? (
        <Skeleton h={240} r="var(--radius-lg)" />
      ) : people.length === 0 ? (
        <EmptyState icon="users" title={t('staff.empty')} />
      ) : (
        <div className="list-card">
          {people.map((p) => (
            <button key={p.id} className={`list-row person-row${p.active ? '' : ' archived'}`} onClick={() => setEditing(p)}>
              <span className="avatar" aria-hidden="true">
                {p.name
                  .split(/\s+/)
                  .map((w) => w[0])
                  .slice(0, 2)
                  .join('')
                  .toUpperCase()}
              </span>
              <span className="list-main">
                <span className="list-title">{p.name}</span>
                <span className="list-sub">@{p.username}</span>
              </span>
              <span className="chips role-chips">
                {[...p.roles]
                  .sort((a, b) => roleOrder(a) - roleOrder(b))
                  .map((r) => (
                    <span key={r} className={`tag${r.startsWith('custom.') ? ' tag-custom' : ''}`}>
                      {roleLabel(r, data.roles)}
                    </span>
                  ))}
              </span>
              {data.sso && p.sso_email ? (
                <span className="tag" title={p.sso_linked ? t('staff.sso_linked', { email: p.sso_email }) : t('staff.sso_waiting', { email: p.sso_email })}>
                  <Icon name="key" size={14} /> {t('staff.sso_tag')}
                </span>
              ) : null}
              {p.two_step ? (
                <span className="tag tag-ok" title={t('staff.two_step_on')}>
                  <Icon name="shieldCheck" size={14} /> {t('staff.two_step')}
                </span>
              ) : null}
              {!p.active ? (
                <StatusBadge tone="neutral" icon="pause" label={t('staff.off')} />
              ) : p.locked ? (
                <StatusBadge tone="warn" icon="alert" label={t('staff.locked')} />
              ) : (
                <StatusBadge tone="ok" icon="check" label={t('staff.active')} />
              )}
            </button>
          ))}
        </div>
      )}
      {editing && data ? (
        <PersonDialog
          person={editing === 'new' ? null : editing}
          data={data}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      ) : null}
    </ManageLayout>
  );
}

function PersonDialog({ person, data, onClose, onSaved }: { person: Person | null; data: Data; onClose: () => void; onSaved: () => void }) {
  const { me } = useSession();
  const { busy, run } = useSave();
  const self = person?.id === me?.userId;
  const ownerAccount = !!person?.roles.includes('owner');
  const readOnly = ownerAccount && !data.can_grant_owner;
  const [name, setName] = useState(person?.name ?? '');
  const [username, setUsername] = useState(person?.username ?? '');
  const [pin, setPin] = useState('');
  const [newPin, setNewPin] = useState(!person);
  const [roles, setRoles] = useState<string[]>(person?.roles ?? ['waiter']);
  const [active, setActive] = useState(person?.active ?? true);
  const [ssoEmail, setSsoEmail] = useState(person?.sso_email ?? '');
  const [tried, setTried] = useState(false);
  const showSso = data.sso || !!person?.sso_email;
  const cleanSso = ssoEmail.trim().toLowerCase();

  const options: RoleInfo[] = [
    ...(data.can_grant_owner ? [{ name: 'owner', permissions: [] }] : []),
    ...[...data.roles].sort((a, b) => roleOrder(a.name) - roleOrder(b.name)),
  ];
  const problem = !name.trim()
    ? t('form.required')
    : !person && !/^[a-z0-9._-]{2,30}$/.test(username.trim().toLowerCase())
      ? t('staff.username_rule')
      : newPin && !/^\d{4,12}$/.test(pin)
        ? t('staff.pin_rule')
        : roles.length === 0
          ? t('staff.need_role')
          : cleanSso && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanSso)
            ? t('staff.sso_email_rule')
            : null;

  const save = () => {
    setTried(true);
    if (problem || readOnly) return;
    void run(async () => {
      if (!person) {
        await action('staff.create', { name: name.trim(), username: username.trim(), pin, roles, ...(cleanSso ? { sso_email: cleanSso } : {}) });
      } else {
        const changes: Record<string, unknown> = {};
        if (name.trim() !== person.name) changes.name = name.trim();
        if (!self && roles.slice().sort().join() !== person.roles.slice().sort().join()) changes.roles = roles;
        if (!self && active !== person.active) changes.active = active;
        if (cleanSso !== (person.sso_email ?? '')) changes.sso_email = cleanSso || null;
        // Someone with two-step sign-in lost their phone: the new PIN opens only their set-up.
        if (newPin && person.two_step) await action('staff.reset_sign_in', { id: person.id, pin });
        else if (newPin) changes.pin = pin;
        if (Object.keys(changes).length) await action('staff.update', { id: person.id, ...changes });
      }
    }, person ? t('menu.saved', { name: name.trim() }) : t('staff.added', { name: name.trim() })).then((ok) => ok && onSaved());
  };

  return (
    <Dialog
      title={person ? person.name : t('staff.add')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={readOnly}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      {readOnly ? <p className="form-hint">{t('staff.owner_only')}</p> : null}
      <div className="form-grid">
        <Field label={t('staff.name')}>
          <input className="input" value={name} maxLength={80} disabled={readOnly} onChange={(e) => setName(e.target.value)} data-autofocus />
        </Field>
        <Field label={t('staff.username')} hint={person ? t('staff.username_fixed') : t('staff.username_hint')}>
          <input
            className="input"
            value={username}
            maxLength={30}
            disabled={!!person}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/\s/g, ''))}
          />
        </Field>
      </div>

      {person && !newPin ? (
        <div className="list-row compact">
          <span className="list-main">
            <span className="list-title">{person.two_step ? t('staff.two_step_on') : t('staff.pin')}</span>
            <span className="list-sub">{person.locked ? t('staff.locked_hint') : person.two_step ? t('staff.two_step_hint') : t('staff.pin_set')}</span>
          </span>
          <Button size="sm" icon="undo" onClick={() => setNewPin(true)} disabled={readOnly || (self && person.two_step)}>
            {person.two_step ? t('staff.reset_sign_in') : person.locked ? t('staff.unlock') : t('staff.reset_pin')}
          </Button>
        </div>
      ) : (
        <Field label={person ? t('staff.new_pin') : t('staff.pin')} hint={person?.two_step ? t('staff.reset_sign_in_hint') : person ? t('staff.new_pin_hint') : t('staff.pin_hint')}>
          <input
            className="input pin-input"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            maxLength={12}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />
        </Field>
      )}

      {showSso ? (
        <Field
          label={t('staff.sso_email')}
          hint={person?.sso_linked && cleanSso === person.sso_email ? t('staff.sso_email_linked') : t('staff.sso_email_hint')}
        >
          <input
            className="input"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            maxLength={254}
            placeholder="name@company.com"
            value={ssoEmail}
            disabled={readOnly}
            onChange={(e) => setSsoEmail(e.target.value.replace(/\s/g, ''))}
          />
        </Field>
      ) : null}

      <fieldset className="option-group">
        <legend>{t('staff.roles')}</legend>
        {self ? <p className="form-hint">{t('staff.own_roles')}</p> : null}
        <p className="form-hint">
          <Link to="/manage/roles">{t('roles.manage_link')}</Link>
        </p>
        <div className="role-list">
          {options.map((r) => {
            const on = roles.includes(r.name);
            return (
              <div key={r.name} className={`role-option${on ? ' on' : ''}`}>
                <input
                  id={`role-${r.name}`}
                  type="checkbox"
                  checked={on}
                  disabled={self || readOnly}
                  onChange={() => setRoles((rs) => (on ? rs.filter((x) => x !== r.name) : [...rs, r.name]))}
                />
                <span>
                  <label htmlFor={`role-${r.name}`}>
                    <strong>{roleLabel(r.name, data.roles)}</strong>
                    {r.rules?.length ? (
                      <span className="tag tag-rule" title={t('roles.limited_hint')}>
                        <Icon name="lock" size={12} /> {t('roles.own_only')}
                      </span>
                    ) : null}
                    <span className="muted">{roleHint(r)}</span>
                  </label>
                  {r.permissions.length ? (
                    <details className="role-perms">
                      <summary>{tn('staff.what_role_can_do', r.permissions.length)}</summary>
                      <ul>
                        {r.permissions.map((p) => (
                          <li key={p.name}>{p.label}</li>
                        ))}
                      </ul>
                    </details>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </fieldset>

      {person && !self ? (
        <div className="list-row compact">
          <span className="list-main">
            <span className="list-title">{t('staff.can_sign_in')}</span>
            <span className="list-sub">{active ? t('staff.can_sign_in_on') : t('staff.can_sign_in_off')}</span>
          </span>
          <Switch checked={active} disabled={readOnly} label={t('staff.can_sign_in')} onChange={() => setActive((a) => !a)} />
        </div>
      ) : null}
      {tried && problem ? (
        <p className="form-error" role="alert">
          {problem}
        </p>
      ) : null}
    </Dialog>
  );
}
